'use client';

import React, { useEffect, useRef } from 'react';
import type { Chart } from '@devexperts/dxcharts-lite';
import { useChartColors } from '@/lib/useChartColors';

export interface Candle {
  time: number; // epoch ms
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

interface Props {
  data: Candle[];
  height?: number;
}

const UP_COLOR = '#22c55e';
const DOWN_COLOR = '#ef4444';

function normalizeCandles(data: Candle[]): Candle[] {
  const valid = data.filter(
    (d) =>
      Number.isFinite(d.time) &&
      Number.isFinite(d.open) &&
      Number.isFinite(d.high) &&
      Number.isFinite(d.low) &&
      Number.isFinite(d.close),
  );
  const sorted = [...valid].sort((a, b) => a.time - b.time);
  const dedup: Candle[] = [];
  for (const c of sorted) {
    if (dedup.length === 0 || dedup[dedup.length - 1].time !== c.time) {
      dedup.push(c);
    } else {
      dedup[dedup.length - 1] = c;
    }
  }
  return dedup;
}

function toDxCandles(candles: Candle[]) {
  return candles.map((d, i) => ({
    id: `${d.time}-${i}`,
    timestamp: d.time,
    open: d.open,
    close: d.close,
    hi: d.high,
    lo: d.low,
    volume: d.volume ?? 0,
  }));
}

function buildColors(textColor: string, gridColor: string) {
  return {
    chartAreaTheme: {
      backgroundMode: 'regular' as const,
      backgroundColor: 'rgba(0,0,0,0)',
      backgroundGradientTopColor: 'rgba(0,0,0,0)',
      backgroundGradientBottomColor: 'rgba(0,0,0,0)',
      gridColor,
    },
    candleTheme: {
      upColor: UP_COLOR,
      downColor: DOWN_COLOR,
      noneColor: textColor,
      upWickColor: UP_COLOR,
      downWickColor: DOWN_COLOR,
      noneWickColor: textColor,
    },
    volume: {
      upCapColor: `${UP_COLOR}cc`,
      downCapColor: `${DOWN_COLOR}cc`,
      noneCapColor: `${textColor}80`,
      upBarColor: `${UP_COLOR}80`,
      downBarColor: `${DOWN_COLOR}80`,
      noneBarColor: `${textColor}40`,
    },
    yAxis: {
      backgroundColor: 'rgba(0,0,0,0)',
      labelTextColor: textColor,
      labelInvertedTextColor: textColor,
      labelBoxColor: 'rgba(0,0,0,0)',
      rectLabelTextColor: textColor,
      rectLabelInvertedTextColor: textColor,
      zeroPercentLine: gridColor,
    },
    xAxis: {
      backgroundColor: 'rgba(0,0,0,0)',
      labelTextColor: textColor,
    },
  };
}

export const CandlestickChart: React.FC<Props> = ({ data, height = 256 }) => {
  const colors = useChartColors();
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<Chart | null>(null);
  const initialDataRef = useRef(data);
  const initialColorsRef = useRef(colors);

  // Mount: lazy-import dxcharts (it touches `window` at import time, so the
  // import must not run on the server) and create the chart with whatever
  // data is on the props at mount time. The parent uses a `key` prop based
  // on the active time range, so a range switch produces a fresh component
  // instance and runs this effect from scratch — no in-place fit-to-data
  // gymnastics needed.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let cancelled = false;

    import('@devexperts/dxcharts-lite').then(({ createChart }) => {
      if (cancelled || !container) return;
      const initialColors = initialColorsRef.current;
      const chart = createChart(container, {
        devexpertsPromoLink: false,
        colors: buildColors(initialColors.textColor, initialColors.gridColor),
      });
      chart.volumes.setVisible(true);
      chart.showSeparateVolumes(true);
      chartRef.current = chart;

      const candles = normalizeCandles(initialDataRef.current);
      if (candles.length > 0) {
        chart.setData({ candles: toDxCandles(candles) });
        // dxcharts' default viewport shows only the most recent ~100 bars,
        // so wide ranges (2Y/5Y/10Y/ALL) all collapse to the same trailing
        // slice unless we explicitly zoom out to cover the full data span.
        if (candles.length >= 2) {
          chart.data.setTimestampRange(
            candles[0].time,
            candles[candles.length - 1].time,
          );
        }
      }
    });

    return () => {
      cancelled = true;
      chartRef.current?.destroy();
      chartRef.current = null;
      // dxcharts can leave canvas children in the container; remove them so
      // the next mount starts clean.
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
    };
  }, []);

  // Live updates within the same range (e.g., intraday ticks).
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const candles = normalizeCandles(data);
    if (candles.length === 0) return;
    chart.setData({ candles: toDxCandles(candles) });
    if (candles.length >= 2) {
      chart.data.setTimestampRange(
        candles[0].time,
        candles[candles.length - 1].time,
      );
    }
  }, [data]);

  // Theme updates without remount.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    chart.setColors(buildColors(colors.textColor, colors.gridColor));
  }, [colors]);

  return <div ref={containerRef} style={{ width: '100%', height }} />;
};
